<script setup lang="ts">
/**
 * The picture, video or YouTube clip on an announcement.
 *
 * Its own component because three kinds with three sets of mobile pitfalls in
 * the middle of a dialog template is unreadable, and because the announcement
 * carousel and any later surface should show them identically.
 *
 * It also owns its own sizing, and that is most of what is written below. Media
 * now sits ABOVE the words on both surfaces that use it — the Updates dialog
 * and the What's New page — so the two old faults have become one visible bug.
 * Anything drawn without a box held open for it pushes the whole announcement
 * down the moment it loads, under the eye of somebody already reading; anything
 * drawn without a ceiling can be a portrait screenshot taller than the screen,
 * which leaves no room for the text it was put there to illustrate. So every
 * kind here is drawn in a box whose height is settled before the bytes arrive
 * and is held to --announcement-media-max-height, unless obeying it would
 * squeeze the box narrower than --announcement-media-min-width.
 */
const props = defineProps<{
  media: Array<{
    kind: "image" | "video" | "youtube";
    url?: string | null;
    embed_url?: string | null;
    name?: string | null;
    width?: number | null;
    height?: number | null;
  }>;
}>();

type MediaEntry = (typeof props.media)[number];

/**
 * Anything that failed to load, so a dead link is not an empty gap.
 *
 * File links are signed and expire; a dialog left open past the expiry would
 * otherwise show a broken-image icon with no explanation.
 */
const broken = ref<Set<string>>(new Set());

/**
 * What shape to hold open for something nothing is yet known about.
 *
 * A guess, and each one is the commonest shape of its kind: clips are filmed in
 * 16/9 far more often than anything else, and a picture with no recorded size
 * is usually a screenshot. The guess decides how much room is held, so getting
 * it wrong costs the difference between two boxes rather than the whole height
 * of a picture that held none.
 */
const RESERVED_RATIO: Record<MediaEntry["kind"], number> = {
  image: 4 / 3,
  video: 16 / 9,
  youtube: 16 / 9,
};

/**
 * The shape of anything that arrived without one, read off the file itself.
 *
 * The server records the width and height of a picture as it is uploaded, but
 * it records nothing at all for a video and nothing for pictures stored before
 * it started doing so. Those would otherwise keep the guessed shape for good,
 * which for a portrait phone recording means a wide letterbox with a sliver of
 * video down the middle. The browser knows the truth as soon as the first bytes
 * land — naturalWidth on a picture, videoWidth once a video has its metadata —
 * so it is asked, and the box settles into the real shape.
 *
 * Keyed by url, which is unique per file within an announcement.
 *
 * Settling is only ever the fallback, because it moves the words below it by
 * whatever the guess got wrong. A recorded size moves nothing. The move is
 * always a bounded one, because both boxes are held between the same ceiling
 * and the same floor, and on a wide screen there is usually none at all: the
 * guess and the truth are both taller than the ceiling there, so both are drawn
 * at exactly it. A very tall shape is the exception, because the floor draws
 * that one past the ceiling — a portrait recording settles somewhat lower than
 * the 16/9 box held for it, rather than landing on the ceiling as the rest do.
 */
const measured = ref<Record<string, number>>({});

/** Recorded shape first, then whatever the file said, then the guess. */
const ratioFor = (entry: MediaEntry) => {
  if (entry.width && entry.height) return entry.width / entry.height;

  const seen = entry.url ? measured.value[entry.url] : null;

  return seen || RESERVED_RATIO[entry.kind];
};

/**
 * The box one item is drawn in: this shape, this tall at most, this wide at
 * least.
 *
 * The height is never written down. It comes from the width and the ratio, and
 * the ceiling is applied as a max-WIDTH of ceiling × ratio — which is the whole
 * trick. A max-height would leave the box its full width, and a picture told to
 * fit inside a box wider than its shape sits in the middle of two grey bars. A
 * max-width narrows the box itself, so a tall screenshot becomes a tall, narrow
 * picture with the right edges and nothing around it, and the words start
 * directly underneath. Nothing is ever cropped, and nothing is stretched.
 *
 * The floor is there because that trick runs away with itself on a tall shape.
 * Width is what the ceiling is paid in, and the taller the item the higher the
 * price, until a portrait screenshot is a strip too narrow to read anything in.
 * So the two are combined with max(): the box is as wide as the ceiling allows
 * or as wide as the floor demands, whichever is the greater. The floor wins
 * only on shapes tall enough to have been ruined by the ceiling, and such an
 * item is then drawn taller than the ceiling — which is the intended trade, and
 * the reason the height is described above as held to the ceiling rather than
 * capped by it. The numbers, and what they cost, are in the stylesheet.
 */
const frameStyle = (entry: MediaEntry) => {
  const ratio = ratioFor(entry);

  return {
    aspectRatio: `${ratio}`,
    maxWidth: `max(calc(var(--announcement-media-max-height) * ${ratio}), var(--announcement-media-min-width))`,
  };
};

const remember = (entry: MediaEntry, width: number, height: number) => {
  // A file with no dimensions of its own — audio in an mp4 container, most
  // likely — reports zero, and a zero ratio would collapse the box entirely.
  if (!entry.url || !width || !height) return;

  measured.value[entry.url] = width / height;
};

const onImageLoad = (event: Event, entry: MediaEntry) =>
  remember(
    entry,
    (event.target as HTMLImageElement).naturalWidth,
    (event.target as HTMLImageElement).naturalHeight,
  );

const onVideoMetadata = (event: Event, entry: MediaEntry) =>
  remember(
    entry,
    (event.target as HTMLVideoElement).videoWidth,
    (event.target as HTMLVideoElement).videoHeight,
  );

const images = computed(() =>
  props.media.filter((entry) => entry.kind === "image" && entry.url),
);

/** Every image, so tapping one opens the gallery at that one. */
const imageUrls = computed(() =>
  images.value.map((entry) => entry.url as string),
);
</script>

<template>
  <!--
    One item gets the full ceiling. Several share a lower one, because the
    ceiling is there to keep the start of the announcement on the first screen
    and three pictures at full height bury it as surely as one very tall one
    would. It lowers the wall rather than removing it — a long gallery still has
    to be scrolled past — but two screenshots now sit above the words instead of
    in place of them.
  -->
  <div
    v-if="media.length"
    class="announcement-media mt-3 grid gap-2"
    :class="{ 'is-stack': media.length > 1 }"
  >
    <template v-for="(entry, index) in media" :key="index">
      <!--
        A picture. el-image with a teleported preview list is this app's own
        lightbox, used in a dozen other places — tapping opens the gallery at
        this one rather than inventing a second way to enlarge something.

        The frame is on the el-image itself rather than on a wrapper around it.
        el-image draws the picture at the full height of whatever it is given,
        so a wrapper holding the shape would leave the picture capped inside a
        box that kept the old height, with an empty strip underneath.

        The ratio is the recorded one where there is one, and the loaded
        picture's own the moment there is not — which is also what makes the
        lazy loading above honest. A picture that reserved no space was always
        judged to be in view, so every picture on a long page was fetched at
        once; with a real box, the ones further down wait their turn.
      -->
      <el-image
        v-if="entry.kind === 'image' && entry.url && !broken.has(entry.url)"
        :src="entry.url"
        :preview-src-list="imageUrls"
        :initial-index="imageUrls.indexOf(entry.url)"
        :preview-teleported="true"
        fit="contain"
        loading="lazy"
        :style="frameStyle(entry)"
        class="announcement-media__frame cursor-zoom-in rounded-lg bg-gray-50 dark:bg-gray-800"
        @load="onImageLoad($event, entry)"
        @error="broken.add(entry.url as string)"
      />

      <!--
        An uploaded video.

        playsinline is not optional: without it iOS takes any play over to its
        own fullscreen player, which drops the person out of the announcement
        they were reading. muted so nothing starts shouting in an office, and
        preload="metadata" so opening the dialog does not pull megabytes for a
        video nobody presses.

        That preload now does a second job. The server records no dimensions for
        a video, so the metadata is the only place its shape is written down,
        and it is what turns the 16/9 box held open here into the right one for
        a portrait recording. object-contain covers the moment in between: until
        the metadata lands the frame is a guess, and a guess must letterbox the
        first frame rather than stretch it.
      -->
      <video
        v-else-if="
          entry.kind === 'video' && entry.url && !broken.has(entry.url)
        "
        :src="entry.url"
        :style="frameStyle(entry)"
        class="announcement-media__frame rounded-lg bg-black object-contain"
        controls
        muted
        playsinline
        preload="metadata"
        @loadedmetadata="onVideoMetadata($event, entry)"
        @error="broken.add(entry.url as string)"
      />

      <!--
        A YouTube clip.

        The aspect-ratio box is what makes it work on a phone: a fixed height
        letterboxes on a narrow screen and overflows on a wide one. Loaded from
        the no-cookie host, and allowed to go fullscreen — without the allow
        list the fullscreen button is drawn and does nothing.

        It takes the same frame as the rest, which is what keeps the ceiling off
        the iframe. Capping the player's height directly would leave this box
        standing at its old height with a black strip under the picture; capping
        the box's width shrinks both together. Its shape is fixed at 16/9 and
        never settles — the player is on another origin and will not be asked.
      -->
      <div
        v-else-if="entry.kind === 'youtube' && entry.embed_url"
        :style="frameStyle(entry)"
        class="announcement-media__frame overflow-hidden rounded-lg bg-black"
      >
        <iframe
          :src="entry.embed_url"
          class="h-full w-full border-0"
          title="Video"
          loading="lazy"
          referrerpolicy="strict-origin-when-cross-origin"
          allow="accelerometer; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
          allowfullscreen
        />
      </div>

      <!-- It was there and will not load. Said, rather than left as a gap. -->
      <p
        v-else-if="entry.url && broken.has(entry.url)"
        class="rounded-lg bg-gray-50 px-3 py-2 text-xs text-gray-500 dark:bg-gray-800 dark:text-gray-400"
      >
        {{ entry.name || "An attachment" }} could not be loaded. Refresh the
        page to try again.
      </p>
    </template>
  </div>
</template>

<style scoped>
/*
 * How tall any one item may be.
 *
 * Measured against the screen rather than against the dialog, because the thing
 * being protected is the reader's first screenful: the words have to start
 * above the fold whether they are in the 540px dialog beside the chat widget,
 * in the 900px one on a desktop, or in a full-width card on the What's New
 * page. A share of the viewport alone is not enough either — 44vh of a tall
 * monitor is half a metre of screenshot — so it is whichever of the two is
 * smaller, and on a short landscape phone that is the share, at about 180px.
 *
 * dvh keeps a phone's retracting address bar out of the measurement, and vh is
 * the fallback for browsers that do not know it. The fallback is written as an
 * @supports refinement rather than as a repeated declaration, which is the
 * usual way of doing it and is what UpdatesModal.vue does two files over. That
 * way round only works on a real property: the browser parses the value, fails
 * to understand dvh, and throws that line away. A custom property is not parsed
 * at all — whatever tokens are given are stored verbatim — so a repeated dvh
 * line would overwrite the vh one even on a browser to which dvh means nothing,
 * and the calc() that reads the property back would then be invalid and leave
 * the frame with no ceiling at all. @supports asks the question in the one form
 * that gets an honest answer here.
 *
 * They are custom properties rather than numbers written into each rule so that
 * a surface with different room can set its own, and so that the stack below is
 * two lines.
 */
.announcement-media {
  --announcement-media-max-height: min(44vh, 420px);
}

.announcement-media.is-stack {
  --announcement-media-max-height: min(30vh, 260px);
}

@supports (height: 1dvh) {
  .announcement-media {
    --announcement-media-max-height: min(44dvh, 420px);
  }

  .announcement-media.is-stack {
    --announcement-media-max-height: min(30dvh, 260px);
  }
}

/*
 * How narrow obeying that ceiling is allowed to make the box.
 *
 * The ceiling is paid for in width, which is a fair price for a landscape
 * picture and a ruinous one for a portrait. A full-bleed phone screenshot is
 * about 9:19.5, so on a 360px phone the 281px ceiling draws it 130px across:
 * two-fifths of the column, the rest of it empty on either side, and not a word
 * of the screenshot legible. A recording of that shape is worse off still.
 * Below roughly 200px Chrome collapses its native controls to little more than
 * a play button — the scrubber goes, so the clip cannot be wound through or
 * scanned — and unlike a picture there is no viewer to tap into and no way back.
 *
 * min() with 100% is what keeps the floor from ever being wider than the column
 * it sits in, on the narrowest phone or in some future narrower surface. It is
 * a floor on the box, not a width: a landscape item is still as wide as its
 * column, because its ceiling-driven width is the greater of the two anyway.
 *
 * A stack floors lower. Two portrait items held to 220px come to some 950px of
 * media before the first word, which is the burial the ceiling exists to
 * prevent; 180px is about the width at which they are still worth looking at.
 *
 * One caveat, because it cannot be read off this file: inside the Updates
 * dialog these same elements are also capped at 45dvh by UpdatesModal.vue, on a
 * selector that outranks anything scoped here. A floored portrait item is
 * taller than that, so there the dialog has the last word and trims it back,
 * leaving the picture with the narrow grey margins a height cap always leaves.
 * The floor is felt in full on the What's New page, which caps nothing.
 */
.announcement-media {
  --announcement-media-min-width: min(100%, 220px);
}

.announcement-media.is-stack {
  --announcement-media-min-width: min(100%, 180px);
}

/*
 * The box itself: as wide as it is allowed to be, and centred once the ceiling
 * has made it narrower than the column it sits in — otherwise a tall screenshot
 * hangs off the left with all of its empty space in one strip down the right.
 *
 * display: block is for that centring. el-image's own root is inline-block,
 * which margin: auto does nothing to.
 */
.announcement-media__frame {
  display: block;
  width: 100%;
  margin-inline: auto;
}

/*
 * The grey square el-image draws while it waits, in our colours rather than its
 * own. Its fill is a light grey from the Element Plus variables, which was
 * invisible while these boxes had no height and would now be a bright panel
 * sitting in a dark announcement for as long as the picture takes to arrive.
 * Transparent lets the frame's own background — grey in daylight, near-black in
 * dark mode — show through instead.
 */
.announcement-media :deep(.el-image__placeholder) {
  background: transparent;
}
</style>
